import { DefaultError, useMutation } from '@tanstack/react-query';
import { useQueryToast } from 'hooks';
import i18next from 'i18next';
import { apiClient } from 'utils';
import { useResponseHandler } from 'utils/service/responseHandler';

export const useCreateProfilePicture = () => {
  const { responseHandler } = useResponseHandler();
  const { loading, dismiss, success } = useQueryToast(
    'useCreateProfilePicture'
  );
  return useMutation<{}, DefaultError, { file: File; id: string }>({
    mutationFn: async body => {
      loading(
        i18next.t('service-message.create.loading', {
          what: i18next.t('artist.profile-picture-what'),
        })
      );

      const formData = new FormData();

      formData.append('file', body.file);

      const response = await apiClient<FormData>({
        method: 'POST',
        url: `Artist/${body.id}/ProfilePicture`,
        body: formData,
        withContentType: false,
      });

      return responseHandler(response, {
        groupName: 'useCreateProfilePicture',
      });
    },
    onSuccess: data => {
      success(
        i18next.t('service-message.create.success', {
          what: i18next.t('artist.profile-picture-what'),
        })
      );
    },
    onError: dismiss,
  });
};

export const useChangeProfilePicture = (id: string) => {
  const { responseHandler } = useResponseHandler();
  const { loading, dismiss, success } = useQueryToast(
    'useChangeProfilePicture'
  );
  return useMutation<{}, DefaultError, File>({
    mutationFn: async file => {
      loading(
        i18next.t('service-message.change.loading', {
          what: i18next.t('artist.profile-picture-what'),
        })
      );

      const formData = new FormData();

      formData.append('file', file);

      const response = await apiClient<FormData>({
        method: 'PUT',
        url: `Artist/${id}/ProfilePicture`,
        body: formData,
        withContentType: false,
      });

      return responseHandler(response, {
        groupName: 'useChangeProfilePicture',
      });
    },
    onSuccess: data => {
      success(
        i18next.t('service-message.change.success', {
          what: i18next.t('artist.profile-picture-what'),
        })
      );
    },
    onError: dismiss,
  });
};

export const useDeleteProfilePicture = (id: string) => {
  const { responseHandler } = useResponseHandler();
  const { loading, dismiss, success } = useQueryToast(
    'useDeleteProfilePicture'
  );
  return useMutation<{}, DefaultError>({
    mutationFn: async () => {
      loading(
        i18next.t('service-message.delete.loading', {
          what: i18next.t('artist.profile-picture-what'),
        })
      );

      const response = await apiClient({
        method: 'DELETE',
        url: `Artist/${id}/ProfilePicture`,
      });

      return responseHandler(response, {
        groupName: 'useDeleteProfilePicture',
      });
    },
    onSuccess: data => {
      success(
        i18next.t('service-message.delete.success', {
          what: i18next.t('artist.profile-picture-what'),
        })
      );
    },
    onError: dismiss,
  });
};
